import type { AxiosResponse } from 'axios'

import configs from '@acre/config'

import request from '../requesters/default'

const { API1_URL, CLIENT_PORTAL_API_URL, CLIENT_PORTAL_OATHKEEPER_API_URL } = configs

// KBA
export const getKbaQuestionnaire = async (id: string, useClientApi?: boolean | null) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  let baseURL
  if (useOathKeeper === 'true') {
    baseURL = `${CLIENT_PORTAL_OATHKEEPER_API_URL}/checks-service`
  } else if (useClientApi) {
    baseURL = `${CLIENT_PORTAL_API_URL}/checks-service`
  } else {
    baseURL = `${API1_URL}/acre/checks-service`
  }

  const response: AxiosResponse<KbaAttempt> = await request.get<KbaAttempt>('/kba', {
    baseURL: baseURL,
    params: { client_id: id },
  })

  return response.data
}

export const submitKba = async (id: string, input: KbaInput, useClientApi?: boolean | null) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  let baseURL
  if (useOathKeeper === 'true') {
    baseURL = `${CLIENT_PORTAL_OATHKEEPER_API_URL}/checks-service`
  } else if (useClientApi) {
    baseURL = `${CLIENT_PORTAL_API_URL}/checks-service`
  } else {
    baseURL = `${API1_URL}/acre/checks-service`
  }

  const response = await request.post<KbaSubmissionResponse>('/kba', input, {
    baseURL: baseURL,
    params: { client_id: id },
  })

  return response.data
}

// Quotation Credit Search
export const runQuotationCreditSearch = async (clientIds: string[]) => {
  const response = await request.post(
    '/equifax_credit_search',
    {},
    {
      baseURL: `${API1_URL}/acre/checks-service`,
      params: { client_id: clientIds, quotation: 'true' },
    },
  )
  return response.status === 200
}

// Credit Report
export const getCreditReport = async (
  useClientApi?: boolean | null,
  clientId?: string | null,
  documentId?: string | null,
) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  let baseURL
  if (useOathKeeper === 'true') {
    baseURL = `${CLIENT_PORTAL_OATHKEEPER_API_URL}/checks-service`
  } else if (useClientApi) {
    baseURL = `${CLIENT_PORTAL_API_URL}/checks-service`
  } else {
    baseURL = `${API1_URL}/acre/checks-service`
  }

  const params: Record<string, string> = {}
  if (!useClientApi && clientId) params.client_id = clientId
  if (documentId) params.doc_id = documentId

  const response: AxiosResponse<CreditReportResponse> = await request.get<CreditReportResponse>('/credit', {
    baseURL: baseURL,
    params,
  })

  return response.data
}

// Credit Refresh
interface CreditRefreshRestResponse {
  status?: string
  reason?: {
    code?: string
    description?: string
  }
}

const CREDIT_REFRESH_STATUSES: CreditRefreshStatus[] = ['AVAILABLE', 'UNAVAILABLE', 'REFRESHED', 'IN_PROGRESS']

// The endpoint returns lowercase statuses; the schema enum is uppercase. An
// unrecognised status maps to null so it degrades to a disabled button rather
// than a schema serialization error
const toCreditRefreshStatus = (status?: string): CreditRefreshStatus | null =>
  CREDIT_REFRESH_STATUSES.find((s) => s === status?.toUpperCase()) ?? null

export const refreshCredit = async (clientId: string, dryRun: boolean): Promise<CreditRefreshResult> => {
  const response: AxiosResponse<CreditRefreshRestResponse> = await request.post<CreditRefreshRestResponse>(
    '/credit/refresh',
    undefined,
    {
      baseURL: `${API1_URL}/acre/checks-service`,
      params: { client_id: clientId, dry_run: dryRun },
    },
  )

  const { status, reason } = response.data
  return {
    status: toCreditRefreshStatus(status),
    reason: reason ?? null,
  }
}

// Suitability Report
// Send client email notification to sign their SR
export const notifyClientFetchSr = async (input: SrNotificationInput) => {
  const baseURL = `${API1_URL}/acre/checks-service`
  const response = await request.post<SrNotificationResponse>('/notify_client_fetch_sr', input, {
    baseURL: baseURL,
  })

  return response.data
}
